import {
    BadgeCheck,
    Building2,
    Clock,
    Database,
    Headphones,
    Lock,
    ShieldCheck,
    Sparkles,
} from 'lucide-react';

const benefits = [
    {
        icon: Building2,
        title: 'Expertise métier',
        text: 'Une équipe d’experts au service de votre réussite',
    },
    {
        icon: ShieldCheck,
        title: 'Orientée résultats',
        text: 'Des solutions concrètes et mesurables',
    },
    {
        icon: Clock,
        title: 'Partenariat durable',
        text: 'Un accompagnement sur le long terme',
    },
    {
        icon: Sparkles,
        title: 'Amélioration continue',
        text: 'Des évolutions régulières pour rester à la pointe',
    },
];

const reassurances = [
    {
        icon: Database,
        title: 'Une base de données complète',
        text: '630 000+ entreprises suisses répertoriées',
    },
    {
        icon: Headphones,
        title: 'Un accompagnement de proximité',
        text: 'Des experts à votre écoute, pas un centre d’appel',
    },
    {
        icon: ShieldCheck,
        title: 'Une plateforme fiable',
        text: 'Conçue pour rester disponible quand vous en avez besoin',
    },
    {
        icon: BadgeCheck,
        title: 'Un support réactif',
        text: 'Une équipe qui vous répond rapidement',
    },
];

const advantages = [
    {
        icon: ShieldCheck,
        title: 'Sécurité & Confidentialité',
        description:
            'Hébergement sécurisé, chiffrement des données et accès strictement contrôlés, conformes à la nLPD (loi fédérale sur la protection des données) et au RGPD pour nos clients traitant des données de résidents européens.',
    },
    {
        icon: Headphones,
        title: 'Accompagnement sur mesure',
        description:
            'Une équipe d’experts juridiques et comptables à votre écoute, pour vous orienter dans vos démarches sans jargon ni complexité inutile.',
    },
    {
        icon: Sparkles,
        title: 'Amélioration continue',
        description:
            'Des évolutions régulières de la plateforme, construites à partir des besoins remontés par nos clients PME.',
    },
];

function AvisSection() {
    return (
        <section className="relative isolate overflow-hidden bg-[linear-gradient(180deg,#FAFAFB_0%,#F5F6F7_100%)] text-[#1A1D23]">
            {/* Grain fin pour la texture */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.02] mix-blend-multiply"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
            />

            {/* Lueur rouge discrète */}
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        'radial-gradient(ellipse 45% 40% at 90% 10%, rgba(178,3,3,0.05) 0%, transparent 60%)',
                }}
            />

            <div className="relative mx-auto grid min-h-[calc(100vh-120px)] max-w-[1200px] grid-cols-1 items-stretch gap-14 px-7 py-16 lg:grid-cols-[0.85fr_1.15fr] lg:py-20">
                {/* COLONNE GAUCHE */}
                <div className="flex h-full max-w-xl flex-col">
                    {/* BLOC 1 — À propos */}
                    <div className="mb-6 inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#b20303]">
                        <span className="h-px w-6 bg-[#b20303]" />
                        À propos de Helvedoc
                    </div>

                    <h1 className="mb-6 text-[24px] font-semibold leading-[1.25] tracking-[-0.02em] text-[#1A1D23] lg:text-[32px]">
                        Une plateforme d’accompagnement pensée pour les PME de
                        Suisse romande
                    </h1>

                    <p className="mb-10 max-w-lg text-[14.5px] leading-6 text-[#5B616E]">
                        Helvedoc accompagne les PME, indépendants, fiduciaires
                        et responsables administratifs de Suisse romande dans
                        leurs démarches juridiques, comptables et de conformité
                        — et leur donne un accès direct aux documents officiels
                        du registre du commerce, comme l’extrait RC.
                    </p>

                    {/* BLOC 2 — Les 3 piliers */}
                    <div className="space-y-4 border-t border-[#E4E6EA] pt-6">
                        {advantages.map((item) => {
                            const Icon = item.icon;

                            return (
                                <div
                                    key={item.title}
                                    className="group flex items-start gap-3"
                                >
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E4E6EA] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition-all duration-500 group-hover:border-[#b20303]/50 group-hover:shadow-[0_8px_20px_rgba(178,3,3,0.12)]">
                                        <Icon
                                            className="h-3.5 w-3.5 text-[#b20303]"
                                            strokeWidth={1.6}
                                        />
                                    </div>

                                    <div>
                                        <h2 className="mb-1 text-[13px] font-semibold tracking-[-0.01em] text-[#1A1D23]">
                                            {item.title}
                                        </h2>

                                        <p className="max-w-xl text-[11.5px] leading-[1.5] text-[#5B616E]">
                                            {item.description}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* BLOC 3 — Réassurance */}
                    <div className="mt-5 overflow-hidden rounded-2xl border border-[#E4E6EA] bg-white shadow-[0_3px_12px_rgba(15,23,42,0.04)]">
                        <div className="grid auto-rows-fr grid-cols-1 md:grid-cols-2">
                            {reassurances.map((item, index) => {
                                const Icon = item.icon;

                                return (
                                    <div
                                        key={item.title}
                                        className={`group flex h-full items-start gap-3 px-4 py-3 transition-colors duration-300 hover:bg-[#FAFAFB] ${
                                            index % 2 === 0
                                                ? 'md:border-r md:border-[#E4E6EA]'
                                                : ''
                                        } ${
                                            index < 2
                                                ? 'border-b border-[#E4E6EA]'
                                                : ''
                                        }`}
                                    >
                                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#b20303]/10">
                                            <Icon
                                                className="h-3.5 w-3.5 text-[#b20303]"
                                                strokeWidth={1.8}
                                            />
                                        </div>

                                        <div className="flex min-w-0 flex-1 flex-col justify-center">
                                            <h3 className="text-[11px] font-semibold leading-tight text-[#1A1D23]">
                                                {item.title}
                                            </h3>

                                            <p className="mt-1 text-[10px] leading-[1.35] text-[#8A8F98]">
                                                {item.text}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* BLOC 4 — Hébergement des données */}
                    <div className="relative mt-4 overflow-hidden rounded-2xl border border-[#E4E6EA] bg-gradient-to-br from-white to-[#FAFAFB] p-4 shadow-[0_1px_3px_rgba(15,23,42,0.03)]">
                        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-[#b20303]/40 to-transparent" />

                        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#b20303]/[0.04] blur-3xl" />

                        <div className="relative flex flex-col items-center gap-3 text-center sm:flex-row sm:items-start sm:text-left">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#b20303] to-[#8a0202] text-white shadow-[0_4px_12px_rgba(178,3,3,0.25)]">
                                <Lock
                                    className="h-[18px] w-[18px]"
                                    strokeWidth={1.8}
                                />
                            </div>

                            <div className="flex-1">
                                <h2 className="mb-1 text-[12.5px] font-semibold text-[#1A1D23]">
                                    Des données protégées
                                </h2>

                                <p className="text-[11.5px] leading-[1.5] text-[#8A8F98]">
                                    Vos données sont hébergées dans des
                                    infrastructures sécurisées et chiffrées,
                                    dans le respect de la nLPD et des standards
                                    européens de protection des données.
                                </p>

                                <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5 sm:justify-start">
                                    <span className="inline-flex items-center gap-1 rounded-full border border-[#E4E6EA] bg-white px-2.5 py-1 text-[9.5px] font-medium text-[#1A1D23] shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                                        <ShieldCheck
                                            className="h-2.5 w-2.5 text-[#b20303]"
                                            strokeWidth={2}
                                        />
                                        nLPD
                                    </span>

                                    <span className="inline-flex items-center gap-1 rounded-full border border-[#E4E6EA] bg-white px-2.5 py-1 text-[9.5px] font-medium text-[#1A1D23] shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                                        <BadgeCheck
                                            className="h-2.5 w-2.5 text-[#b20303]"
                                            strokeWidth={2}
                                        />
                                        RGPD
                                    </span>

                                    <span className="inline-flex items-center gap-1 rounded-full border border-[#E4E6EA] bg-white px-2.5 py-1 text-[9.5px] font-medium text-[#1A1D23] shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                                        <Lock
                                            className="h-2.5 w-2.5 text-[#b20303]"
                                            strokeWidth={2}
                                        />
                                        Données chiffrées
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* COLONNE DROITE */}
                <div className="flex h-full min-h-0 flex-col gap-8">
                    {/* Image principale */}
                    <div className="relative flex min-h-[320px] flex-1">
                        <div className="absolute -inset-8 rounded-[36px] bg-[#b20303]/[0.06] blur-3xl" />

                        <div className="relative flex h-full w-full overflow-hidden rounded-[20px] border border-[#E4E6EA] bg-white shadow-[0_30px_90px_rgba(15,23,42,0.1)] transition-shadow duration-700 hover:shadow-[0_36px_100px_rgba(15,23,42,0.14)]">
                            <div className="absolute inset-x-0 top-0 z-10 h-[2px] bg-gradient-to-r from-[#b20303]/0 via-[#b20303] to-[#b20303]/0" />

                            <img
                                src="/images/background-helvedoc-3.jpeg"
                                alt="Plateforme d’accompagnement Helvedoc"
                                className="h-full w-full object-cover"
                            />
                        </div>
                    </div>

                    {/* BLOC 5 — Les 4 valeurs */}
                    <div className="grid auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        {benefits.map(({ icon: Icon, title, text }) => (
                            <div
                                key={title}
                                className="group relative flex h-full overflow-hidden rounded-2xl border border-[#E4E6EA] bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-1 hover:border-[#b20303]/50 hover:shadow-[0_16px_32px_rgba(15,23,42,0.08)]"
                            >
                                <div className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-[#b20303] transition-transform duration-500 group-hover:scale-x-100" />

                                <div className="pointer-events-none absolute -left-6 -top-6 h-24 w-24 rounded-full bg-[#b20303]/0 blur-2xl transition-all duration-500 group-hover:bg-[#b20303]/10" />

                                <div className="relative flex h-full w-full flex-col items-start gap-3.5">
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F5F6F7] text-[#8A8F98] ring-1 ring-[#E4E6EA] transition-all duration-500 group-hover:bg-[#b20303] group-hover:text-white group-hover:ring-[#b20303]/30">
                                        <Icon
                                            className="h-5 w-5"
                                            strokeWidth={1.8}
                                        />
                                    </div>

                                    <div className="flex w-full min-w-0 flex-1 flex-col">
                                        <h3 className="mb-1 text-[13px] font-semibold tracking-tight text-[#1A1D23]">
                                            {title}
                                        </h3>

                                        <p className="text-[11.5px] leading-[1.5] text-[#8A8F98]">
                                            {text}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default AvisSection;